import { ImageProps } from "next/image";
import { useCallback, useEffect, useState } from "react";

import { Button } from "@/components/atoms/buttons/Button";
import { Img } from "@/components/atoms/img/Img";
import { CalendarOptionsArea } from "@/components/editors/template-blocks/service-menu/service-menu-items/CalendarOptionArea";
import { useHandleFieldValueChange } from "@/states/service-form/operations";
import {
  useCannotOrderReasons,
  useConfirmInputList,
  useServiceFormUiState,
} from "@/states/service-form/selectors";
import {
  FieldValueChangeHandler,
  ServiceFormServiceInputKey,
  ServiceMenuDetail,
} from "@/states/service-form/types";
import { getCalendarTimeRequired, isEmptyValue } from "@/utils/common";
import { formatNumber } from "@/utils/number";

interface ScheduleCalendarViewItemsProps {
  schedules: ServiceMenuDetail[];
  handleSubmitCalendar: () => void;
  isConfirm?: boolean;
}

export function ScheduleCalendarViewItems({
  schedules,
  handleSubmitCalendar,
  isConfirm,
}: ScheduleCalendarViewItemsProps) {
  const onFieldValueChange =
    useHandleFieldValueChange<ServiceFormServiceInputKey>();

  const confirmInputList = useConfirmInputList();
  const serviceFormUiState = useServiceFormUiState();

  const [selectedMenuId, setSelectedMenuId] = useState<number>();

  useEffect(() => {
    if (schedules && schedules.length === 1) {
      onFieldValueChange("serviceMenuId", Number(schedules[0].id));``;
    }
  }, [onFieldValueChange, schedules]);

  const onSubmitSelectMenu = (value: number) => {
    if (serviceFormUiState.status === "Loaded") {
      if (value !== serviceFormUiState.inputs.service.serviceMenuId) {
        const currentMenu = schedules.find((item) => item.id === value);
        const ids = currentMenu?.options.filter((item) => selectedOptionsIds?.includes(item.id)).map((opt) => opt.id) || [];
        onFieldValueChange("serviceMenuOptionIds", ids);
      } else {
        onFieldValueChange("serviceMenuOptionIds", selectedOptionsIds ?? []);
      }
      setSelectedMenuId(value);
    }
    onFieldValueChange("serviceMenuId", value);
  };

  useEffect(() => {
    if (serviceFormUiState?.status === "Loaded" && !!selectedMenuId) {
      handleSubmitCalendar();
    }
  }, [handleSubmitCalendar, selectedMenuId, serviceFormUiState?.status]);

  const [selectedOptionsIds, setSelectedOptionsIds] = useState<number[] | undefined>(isConfirm ? confirmInputList.serviceDraft?.serviceMenuOptionIds : confirmInputList.service?.serviceMenuOptionIds);

  const handleChangeOptionIds = useCallback((menuId: number, values: number[]) => {
    if (serviceFormUiState.status === "Loaded") {
      if (menuId !== serviceFormUiState.inputs.service.serviceMenuId) {
        const currentMenu = schedules.find((item) => item.id === menuId);
        const ids = currentMenu?.options.filter((item) => values.includes(item.id)).map((opt) => opt.id) || [];
        setSelectedOptionsIds(ids);
        onFieldValueChange("serviceMenuOptionIds", ids);
      } else {
        setSelectedOptionsIds(values);
        onFieldValueChange("serviceMenuOptionIds", values);
      }
    }
  }, [schedules, serviceFormUiState]);

  return (
    <div className="space-y-4">
      {schedules?.map((item) => {
        return (
          <div key={item.id} className="flex items-center bg-white border rounded-[4px] px-[14px] py-[11px] hover:border-[#3ABCA8] border-[#DFDFE2] w-full">
            <ItemContent
              item={item}
              onSubmitSelectMenu={onSubmitSelectMenu}
              isConfirm={isConfirm}
              selectedOptionsIds={selectedOptionsIds}
              onChangeOptionIds={handleChangeOptionIds}
            />
          </div>
        );
      })}
    </div>
  );
}

interface ItemContentProps {
  item: ServiceMenuDetail;
  onSubmitSelectMenu: (value: number) => void;
  selectedOptionsIds?: number[];
  isConfirm?: boolean;
  onChangeOptionIds: (menuId:number, values: number[]) => void;
}
const ItemContent = ({
  item,
  onSubmitSelectMenu,
  isConfirm,
  selectedOptionsIds,
  onChangeOptionIds,
}: ItemContentProps) => {
  const onFieldValueChange = useHandleFieldValueChange();
  const imgSrc = (url: string | undefined) => {
    return !url || isEmptyValue(url) ? "" : url;
  };
  const { name, description, imageUrl, options, price, timeRequired, isShowTime, isShowFree } = item;
  const cannotOrderReasons = useCannotOrderReasons();

  return (
    <div className="w-full">
      <p className="mb-2 text-[16px] font-bold leading-5 text-gray-900 break-word">
        {name}
      </p>
      <p className="mb-2 w-full text-right">
        {
          isShowTime ? <span className="text-[14px]">{`${
            getCalendarTimeRequired(timeRequired) ?? ""
          }分`}</span> : null
        }
      
        {price && Number(price) > 0 ? (
          <span className="ml-2 text-[16px] font-bold">{`${formatNumber(
            Number(price),
          )}円 (税込)`}</span>
        ) : (
          <>
            {isShowFree ? <span className="ml-2 text-[16px] font-bold">無料</span> : null}
          </>
        )}
      </p>
      {(imageUrl || description) && (
        <div className="flex flex-wrap gap-x-3 pt-4 pb-2 mb-2 w-full border-t border-[#EFEFEF] md:flex-nowrap">
          {imageUrl && (
            <div className="w-full text-center md:w-1/2 shrink-0">
              <Img
                src={imgSrc(imageUrl)}
                className="w-full"
                imageNodeProps={
                  { objectFit: "contain", priority: true } as ImageProps
                }
              ></Img>
            </div>
          )}
          <div className="grow w-full text-left">
            <p className="text-[14px] text-[#626262] break-word">
              {description}
            </p>
          </div>
        </div>
      )}

      <div className="py-2 mt-2 space-y-4 border-t border-[#EFEFEF]">
        <CalendarOptionsArea
          isForm={true}
          selectedOptionsIds={selectedOptionsIds}
          onChangeOptionIds={(values) => {
            onChangeOptionIds(item.id, values);
          }}
          options={options}
          onFieldValueChange={onFieldValueChange as FieldValueChangeHandler}
          menu={item}
        />
        {cannotOrderReasons.length > 0 && (
          <div className="mb-6 form-error-text">
            {cannotOrderReasons.map((reason, index) => (
              <p key={index} className="text-xl text-center text-accent">
                {reason}
              </p>
            ))}
          </div>
        )}
        {!isConfirm && (
          <Button
            className="!w-full bg-[#3ABCA8] !h-[56px] text-xl !text-white"
            label="このメニューに申し込む"
            onClick={() => onSubmitSelectMenu(item.id)}
            disabled={cannotOrderReasons.length > 0}
          />
        )}
      </div>
    </div>
  );
};
